import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
} from "@angular/core";
import { debounce } from "lodash";
import { MedNotificationType, NgClassType, NgStyleType } from "@mk/med-ui";

import { MedNotification, MedNotificationPositions } from "../types";
import { MedNotificationCloseEvent } from "../typesLocal";
import { DEFAULT_HIDE_DURATION, DEFAULT_SHOW_DURATION } from "../constants";
import { medNotificationItemPositionMap } from "./medNotificationItemPosition.map";

const TIMEOUT = 100;

@Component({
  selector: "med-notification-item",
  templateUrl: "./med-notification-item.component.html",
  styleUrls: ["./med-notification-item.component.scss"],
})
export class MedNotificationItemComponent implements AfterViewInit, OnDestroy {
  constructor(public container: ElementRef) {
  }

  private _onResize = debounce(() => {
    this.height = 0;
    setTimeout(() => {
      this._calcHeight();
    }, 0);
  }, TIMEOUT);

  @Input() notification!: MedNotification;
  @Input() index!: number;
  @Input() template!: TemplateRef<any>;
  @Input() lifeTime!: number;
  @Input() closable = false;
  @Input() position!: MedNotificationPositions;
  @Output() close: EventEmitter<MedNotificationCloseEvent> = new EventEmitter();

  timeout: any;
  visible = false;
  closing = false;
  height = 0;
  defaultType: MedNotificationType = "info";

  @HostBinding("class") get class(): NgClassType {
    return {
      "med-notification-item": true,
      "visible": this.visible,
      "closing": this.closing,
    };
  }

  @HostBinding("style") get style(): NgStyleType {
    const { showDuration, hideDuration } = this.notification;

    return {
      "--show-duration": `${showDuration || DEFAULT_SHOW_DURATION}ms`,
      "--hide-duration": `${hideDuration || DEFAULT_HIDE_DURATION}ms`,
      "height": this.height ? `${this.height}px` : "unset",
      "align-items": medNotificationItemPositionMap.get(this.position),
    };
  }

  @HostListener("window:resize") onResize() {
    this._onResize();
  }

  ngAfterViewInit() {
    this._calcHeight();
    setTimeout(() => {
      this.visible = true;
    });

    this._initCloseTimeout();
  }

  ngOnDestroy() {
    if (!this.timeout) {
      return;
    }

    clearTimeout(this.timeout);
    this.timeout = null;
  }

  closeClicked(): void {
    this.close.emit({
      index: this.index,
      notification: this.notification,
    });
  }

  private _calcHeight() {
    this.height = this.container.nativeElement.getBoundingClientRect().height;
  }

  private _initCloseTimeout() {
    if (this.notification.lifeTime === "infinite") {
      return;
    }

    this.timeout = setTimeout(() => {
      this._closing();
    }, this.notification.lifeTime || this.lifeTime);
  }

  private _closing() {
    this.closing = true;
    const duration = this.notification.hideDuration || DEFAULT_HIDE_DURATION;

    setTimeout(() => {
      this.closing = false;
      this.visible = false;

      this.close.emit({
        index: this.index,
        notification: this.notification,
      });
    }, duration);
  }
}
